import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  calculateZoomFromDistance,
  getNeighbourhoodSuggestions,
} from "common/apis";
import { useSearchPageParams } from "common/hooks";
import { useCustomToast } from "@/hooks/useCustomToast";
import { mapCenterAtom } from "@/state/atoms";
import { useAtom } from "jotai";
import { Loader2 } from "lucide-react";
import { Helmet } from "react-helmet";

export const Route = createFileRoute("/share-token/$token")({
  component: ShareTokenHandler,
});

function ShareTokenHandler() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { setSearchPageParams } = useSearchPageParams();
  const [, setMapCenter] = useAtom(mapCenterAtom);
  const [isLoading, setIsLoading] = useState(true);
  const showToast = useCustomToast();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple calls
    if (hasLoadedRef.current || !token) {
      return;
    }

    const loadCuratedSearch = async () => {
      try {
        hasLoadedRef.current = true;
        setIsLoading(true);

        // Fetch the curated search details using the share_token
        const result = await getNeighbourhoodSuggestions(token);

        if (
          !result ||
          result.count === 0 ||
          !result.searches ||
          result.searches.length === 0
        ) {
          showToast("Unable to load shared search", "error");
          navigate({ to: "/search" });
          return;
        }

        const savedSearch = result.searches[0];
        const filters = savedSearch.filters;

        // Extract search parameters from the saved search
        const searchLat = filters.search_latitude;
        const searchLng = filters.search_longitude;
        const searchRadius = filters.search_radius;
        const businessTypes = filters.business_types || [];
        const accessibilities = filters.accessibilities || [];
        const searchTerm = savedSearch.search_term || "";

        // Set map center if coordinates are available
        if (searchLat && searchLng) {
          setMapCenter({
            lat: parseFloat(searchLat),
            lng: parseFloat(searchLng),
          });
        }

        // Calculate zoom level based on radius
        const radiusInKm = searchRadius ? parseFloat(searchRadius) : 2;
        const zoomLevel = calculateZoomFromDistance(radiusInKm);

        // Build the search parameters matching the homepage defaults
        const searchParams: any = {
          search: searchTerm || "",
          latitude: searchLat || null,
          longitude: searchLng || null,
          userlatitude: null,
          userlongitude: null,
          types: businessTypes.length > 0 ? businessTypes.join("|") : "",
          accessibilities:
            accessibilities.length > 0 ? accessibilities.join("|") : "",
          showVenueOptions: "All",
          addinfo: "",
          placesToReplaceString: "",
          searchThisAreaClicked: "no",
          autoZoom: "yes",
          zoom: zoomLevel.toString(),
          new_map: "1",
          tagging_mode: "off",
          mapping_status: undefined,
          progressive_update: "yes",
          saved_place_id: "",
          saved_place_user_id: "",
          saved_place_sortby: "",
          unixTimestamp: Math.floor(Date.now() / 1000),
          includeGoogleFallback: "false",
        };

        // Set the search parameters
        setSearchPageParams(searchParams);

        // Navigate to search page with the search parameters applied
        navigate({ to: "/search" });

        // Show success message
        showToast(`Loaded: ${savedSearch.name || "Shared search"}`, "success");
      } catch (error) {
        console.error("Error loading curated search:", error);
        showToast("Error loading shared search", "error");
        navigate({ to: "/search" });
      } finally {
        setIsLoading(false);
      }
    };

    loadCuratedSearch();
  }, [token]);

  return (
    <>
      <Helmet>
        <title>Loading Shared Search | Sociability</title>
      </Helmet>
      <div className="h-[calc(100vh-90px)] bg-bg-brand-secondary-default flex flex-col items-center justify-center">
        {isLoading && (
          <>
            <Loader2
              className="h-12 w-12 animate-spin text-fg-brand-subtle mb-4"
              aria-hidden="true"
            />
            <p
              className="text-fg-neutral-strong title-s-semibold"
              role="status"
              aria-live="polite"
            >
              Loading shared search...
            </p>
          </>
        )}
      </div>
    </>
  );
}
